import StudentConfirmDialog, { StudentConfirmDialogPropsType } from '@/components/StudentConfirmDialog';
import TagsInput from '@/components/TagsInput';
import Upload from '@/components/Upload';
import Constants from '@/Constants';
import { FileUploadData } from '@/hooks/upload/FileUpload';
import Category from '@/models/Category';
import { QuizContent } from '@/models/QuizContent';
import { QuizData } from '@/models/QuizData';
import { ApplicationState } from '@/store';
import { getCategoriesRequest } from '@/store/category/action';
import { setFloatingCourseActionsConfiguration } from '@/store/common/actions';
import { FloatingCourseActionsBarConfiguration } from '@/store/common/types';
import { clearQuiz, getAutoExecutableQuizRequest, postQuizRequest } from '@/store/quiz/action';
import { mdiDiamondStone } from '@mdi/js';
import Icon from '@mdi/react';
import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import Switch from 'react-switch';
import AutoExecuteInfoModal from './AutoExecuteInfoModal';
import { formInitialState, QuizForm, quizSchema } from './formConfig';
import './style.scss';

interface ConfirmDialogState {
    scormAlert: string;
    leftButtonTitle: string;
    rightButtonTitle: string;
    rightClick: () => void;
    leftClick: () => void;
}

interface TypeData {
    type: string;
    description: string;
    switchVal: boolean;
}

const QuizInfo: React.FC = () => {
    const dispatch = useDispatch();
    const { id } = useParams<{ id: string }>();
    const history = useHistory();

    const { strings, fetchedCategories, identity, success, quizContent, quiz, autoExecutableQuiz, colors } = useSelector(
        (state: ApplicationState) => ({
            strings: state.locale.data,
            fetchedCategories: state.categories.data.categories,
            personId: state.identity.data.person.id,
            identity: state.identity.data,
            quizContent: state.quiz.data.quizData.quizContent,
            quiz: state.quiz.data.quizData.quiz,
            autoExecutableQuiz: state.quiz.data.autoExecutable,
            success: state.quiz.success,
            colors: state.appOrganization.data.colors,
        }),
    );

    const [canAddingSecondCategory, setCanAddingSecondCategory] = useState<boolean>(false);
    const [addingSecondCategory, setAddingSecondCategory] = useState<boolean>(false);
    const [formState, setFormState] = useState(formInitialState);
    const { mainImage, name, author, description, locale, categories, tags }: QuizForm = formState;
    const [formIsValid, setFormIsValid] = useState<boolean>(false);
    const [onNextPressed, setOnNextPressed] = useState<boolean>(false);
    const [onSavePressed, setOnSavePressed] = useState<boolean>(false);

    const [isQuizOpen, setIsQuizOpen] = useState<boolean>(true);
    const [autoExecute, setAutoExecute] = useState<boolean>(false);
    const [showAutoExecuteWarning, setShowAutoExecuteWarning] = useState<boolean>(false);

    const [notifyUsers, setNotifyUsers] = useState<boolean>(false);

    const [showConfirmation, setShowConfirmation] = useState(false);
    const [modalConfig, setModalConfig] = useState<StudentConfirmDialogPropsType>({} as StudentConfirmDialogPropsType);

    const typeData: TypeData[] = [
        { type: 'READY', description: strings.PROGRAM_STATUS_CLOSE, switchVal: false },
        { type: 'BUILDING', description: strings.PROGRAM_STATUS_OPEN, switchVal: true },
    ];

    const manageQuizBasePath = '/contents/buzz/tests';

    useEffect(() => {
        if (id) {
            // TODO: Update
        } else {
            dispatch(clearQuiz());
        }
    }, [dispatch, id]);

    useEffect(() => {
        const { orgId, applicationId } = identity.user;
        dispatch(getAutoExecutableQuizRequest({ organizationId: orgId, applicationId }));
        dispatch(getCategoriesRequest({ orgId, applicationId }));
        dispatch(
            setFloatingCourseActionsConfiguration({
                currentStep: 1,
                nextButtonTitle: strings.FORWARD,
                onNext: () => setOnNextPressed(true),
                onPrevious: () => {},
                onSave: () => setOnSavePressed(true),
                saveDisabled: false,
                previousButtonTitle: strings.BACK,
                saveButtonTitle: quizContent.id ? strings.SAVE_AND_CLOSE : strings.SAVE_TO_DRAFT,
                totalSteps: 4,
                nextDisabled: formIsValid ? false : true,
                previewDisabled: true,
                blockUi: false,
            }),
        );
    }, [dispatch, identity.user.orgId, identity.user.applicationId, strings._LANGUAGE]);

    useEffect(() => {
        quizSchema
            .validate(formState)
            .then(() => setFormIsValid(true))
            .catch(() => setFormIsValid(false));
    }, [formState]);

    useEffect(() => {
        disableSaveAndNavButtons(!formIsValid);
    }, [formIsValid, strings._LANGUAGE, dispatch]);

    const handleSaveNext = () => {
        if (id) {
            //Não foi implementado a feature do UPDATE
            history.push(`${manageQuizBasePath}/${id}/results`);
            return;
        }

        disableSaveAndNavButtons(true);

        dispatch(
            setFloatingCourseActionsConfiguration({
                nextButtonTitle: strings.LOADING,
                blockUi: true,
            } as FloatingCourseActionsBarConfiguration),
        );

        const quizContent = {
            '@class': '.QuizContent',
            prerequisites: false,
            mandatory: false,
            masteryScore: null,
            type: 'QuizContent',
            visibility: false,
            totalRating: 0,
            status: 'DRAFT',
            identifier: `quiz_${new Date().getTime().toString()}`,
            applicationId: identity.user.applicationId,
            organizationId: identity.user.orgId,
            locale,
            description,
            mainImage,
            name,
            author,
            tags,
            categories,
            moduleId: Constants.BUZZ_MODULE_ID,
            notifyUsers,
        } as QuizContent;

        dispatch(postQuizRequest({ quiz: { autoExecute }, quizContent } as QuizData));
    };

    useEffect(() => {
        if (onSavePressed || onNextPressed) {
            handleSaveNext();
        }
    }, [onSavePressed, onNextPressed]);

    const handleOnChange = useCallback(
        (fieldName: string, value: string | object | null | undefined, index?: number) => {
            if (fieldName === 'categories' && index !== undefined) {
                const currentCategory = fetchedCategories && fetchedCategories.find((category: Category) => category.id === Number(value));
                const tmpCategories: Category[] = [...categories];

                if (currentCategory) {
                    tmpCategories[Number(index)] = currentCategory;
                } else {
                    tmpCategories.splice(Number(index), 1);
                }

                setCanAddingSecondCategory(tmpCategories.length > 0);
                value = tmpCategories;
            }
            setFormState((prevState) => ({ ...prevState, [fieldName]: value }));
        },
        [fetchedCategories, categories],
    );

    const renderCategoriesOptions = (categoryIndex: number) => {
        if (fetchedCategories) {
            const nonCurrentIndex = categories.findIndex((cat: Category, index: number) => categoryIndex !== index);

            return fetchedCategories
                .filter((category: Category) => !categories[nonCurrentIndex] || categories[nonCurrentIndex].id !== category.id)
                .map((category: Category) => (
                    <option key={category.id} value={category.id}>
                        {category.name}
                    </option>
                ));
        }
        return null;
    };

    useEffect(() => {
        if (quizContent && quizContent.id) {
            handleOnChange('name', quizContent.name);
            handleOnChange('author', quizContent.author);
            handleOnChange('mainImage', quizContent.mainImage);
            handleOnChange('description', quizContent.description || '');
            handleOnChange('locale', quizContent.locale);
            handleOnChange('tags', quizContent.tags && quizContent.tags.length > 0 ? quizContent.tags : []);
            setIsQuizOpen(quizContent.type === 'BUILDING');

            if (quizContent.categories) {
                setCanAddingSecondCategory(true);

                if (quizContent.categories.length > 1) {
                    setAddingSecondCategory(true);
                }
                handleOnChange('categories', quizContent.categories);
            }

            dispatch(
                setFloatingCourseActionsConfiguration({
                    saveButtonTitle: strings.SAVE_AND_CLOSE,
                    saveDisabled: false,
                    nextDisabled: false,
                } as FloatingCourseActionsBarConfiguration),
            );
        }
    }, [quizContent, fetchedCategories]);

    useEffect(() => {
        if (success) {
            redirect();
        }
    }, [success]);

    useEffect(() => {
        if (!canAddingSecondCategory) {
            setAddingSecondCategory(false);
        }
    }, [canAddingSecondCategory]);

    useEffect(() => {
        if (!addingSecondCategory) {
            const categoriesUpdated: Category[] = [...categories];
            categoriesUpdated.splice(1, 1);

            handleOnChange('categories', categoriesUpdated);
        }
    }, [addingSecondCategory]);

    const redirect = () => {
        if (onSavePressed) {
            history.push('/contents/buzz/tests');
        }

        if (onNextPressed) {
            history.push(`/contents/buzz/tests/${quiz.id}/results`);
        }
    };

    const disableSaveAndNavButtons = (disabled: boolean): void => {
        dispatch(
            setFloatingCourseActionsConfiguration({
                saveDisabled: disabled,
                nextDisabled: disabled,
            } as FloatingCourseActionsBarConfiguration),
        );
    };

    const onClickTitleHandler = () => {
        setModalConfig({
            rightButtonTitle: strings.I_GOT_IT,
            rightClick: () => setShowConfirmation(false),
            rightButtonStyle: { width: '100%' } as CSSProperties,
            title: strings.SHOW_VALUE,
            titleStyle: { fontSize: 24, color: '#4D4D4D' } as CSSProperties,
            content: strings.USERS_ENGAGE_MORE,
            contentStyle: { fontSize: 18, color: '#4D4D4D', fontWeight: 400 } as CSSProperties,
            useMdi: true,
            icon: <Icon horizontal path={mdiDiamondStone} style={{ width: '4rem', height: '4rem', color: '#B3B3B3', marginBottom: 15 }} />,
        } as StudentConfirmDialogPropsType);
        setShowConfirmation(true);
    };

    const onClickAutoExecuteHandler = () => {
        setModalConfig({
            rightButtonTitle: strings.I_GOT_IT,
            rightClick: () => setShowConfirmation(false),
            rightButtonStyle: { width: '100%' } as CSSProperties,
            title: strings.INCREASE_ENGAGEMENT,
            titleStyle: { fontSize: 24, color: '#4D4D4D' } as CSSProperties,
            content: strings.INCREASE_ENGAGEMENT_DESCRIPTION,
            contentStyle: { fontSize: 18, color: '#4D4D4D', fontWeight: 400 } as CSSProperties,
            icon: 'show_chart',
            iconStyle: { fontSize: 64, color: '#B3B3B3', marginBottom: 15 } as CSSProperties,
        } as StudentConfirmDialogPropsType);
        setShowConfirmation(true);
    };

    useEffect(() => {
        handleOnChange('type', typeData[+isQuizOpen].type);
    }, [isQuizOpen, quizContent]);

    useEffect(() => {
        if (autoExecutableQuiz.id) {
            setShowAutoExecuteWarning(true);
        }
    }, [autoExecutableQuiz.id]);

    return (
        <div className="create-quiz-info position-relative">
            <StudentConfirmDialog
                content={modalConfig.content}
                contentStyle={modalConfig.contentStyle}
                icon={modalConfig.icon}
                iconStyle={modalConfig.iconStyle}
                rightButtonStyle={modalConfig.rightButtonStyle}
                rightButtonTitle={modalConfig.rightButtonTitle}
                rightClick={() => modalConfig.rightClick()}
                show={showConfirmation}
                title={modalConfig.title}
                titleStyle={modalConfig.titleStyle}
                useMdi={modalConfig.useMdi}
            />

            <span className="create-quiz-info__warning">* {strings.REQUIRED_FIELDS}</span>
            <div className="input-section required">
                <h1>{strings.HEADER}</h1>
                <Upload
                    onComplete={(e: FileUploadData) => handleOnChange('mainImage', e.key)}
                    onRemove={() => handleOnChange('mainImage', null)}
                    previewFile={{ uri: mainImage, name: mainImage }}
                    typeAccepted="IMAGE"
                />
            </div>
            <div className="input-section required">
                <div className="d-flex align-items-center">
                    <h1 className="warning">{strings.TITLE}</h1>
                    <i className="material-icons-outlined align-middle information-icon title-icon" onClick={onClickTitleHandler}>
                        {Constants.icons.ERROR_OUTLINE}
                    </i>
                </div>
                <input
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('name', e.target.value)}
                    placeholder={strings.TYPE_TITLE}
                    type="text"
                    value={name}
                />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.AUTHOR}</h1>
                <input
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleOnChange('author', e.target.value)}
                    placeholder={strings.TYPE_AUTHOR}
                    type="text"
                    value={author}
                />
            </div>
            <div className="input-section">
                <h1>{strings.DESCRIPTION}</h1>
                <textarea
                    className="form-control"
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => handleOnChange('description', e.target.value)}
                    placeholder={strings.INSERT_QUIZ_DESCRIPTION_TO_HELP}
                    rows={3}
                    value={description}
                />
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.SELECT_LANGUAGE}</h1>
                <div className="container button-group-content-type">
                    <button
                        className={`btn btn-light ${locale === 'pt' ? 'active' : ''}`}
                        disabled={!!quizContent.id}
                        onClick={() => handleOnChange('locale', 'pt')}
                        type="button"
                    >
                        <span>{strings.LANGUAGE_PT}</span>
                    </button>

                    <button
                        className={`btn btn-light ${locale === 'en' ? 'active' : ''}`}
                        disabled={!!quizContent.id}
                        onClick={() => handleOnChange('locale', 'en')}
                        type="button"
                    >
                        <span>{strings.LANGUAGE_EN}</span>
                    </button>
                </div>
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.CATEGORY}</h1>
                <div className="categories">
                    <select
                        className="form-control"
                        name="category1"
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleOnChange('categories', e.target.value, 0)}
                        value={categories && categories[0] ? categories[0].id : ''}
                    >
                        <option value={strings.SELECT_CATEGORY}>{strings.SELECT_CATEGORY}</option>
                        {renderCategoriesOptions(0)}
                    </select>
                    {addingSecondCategory ? (
                        <div className="categories__second">
                            <select
                                className="form-control"
                                name="category2"
                                onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleOnChange('categories', e.target.value, 1)}
                                value={categories && categories[1] ? categories[1].id : ''}
                            >
                                <option value={strings.SELECT_CATEGORY}>{strings.SELECT_CATEGORY}</option>
                                {renderCategoriesOptions(1)}
                            </select>
                            <button
                                className="btn btn-link"
                                onClick={() => {
                                    setAddingSecondCategory(false);
                                }}
                            >
                                <i className="material-icons-outlined align-middle">{Constants.icons.DELETE}</i>
                                {strings.REMOVE}
                            </button>
                        </div>
                    ) : (
                        <div className="d-flex">
                            {canAddingSecondCategory && (
                                <button className="btn btn-link" onClick={() => setAddingSecondCategory(true)}>
                                    <span>{strings.ADD_CATEGORY}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <div className="input-section required">
                <h1 className="warning">{strings.TAGS}</h1>
                <div className="tags">
                    <TagsInput selectedTags={tags} setTags={handleOnChange} />
                </div>
            </div>

            <div className="input-section">
                <h1 className="highlighted">{strings.WHEN_THIS_TEST_SHOULD_APPEAR}</h1>
                <div className="switch-section">
                    <Switch
                        checked={autoExecute}
                        checkedIcon={false}
                        disabled={false}
                        handleDiameter={20}
                        height={14}
                        offColor="#d1d1d1"
                        offHandleColor="#b3b3b3"
                        onChange={() => setAutoExecute(!autoExecute)}
                        onClick={() => setAutoExecute(!autoExecute)}
                        onColor={colors[0]}
                        onHandleColor={colors[0]}
                        uncheckedIcon={false}
                        width={30}
                    />

                    <span>{strings.OPEN_WHEN_ACCESSING_APPLICATION}</span>
                    <i className="material-icons-outlined align-middle information-icon" onClick={onClickAutoExecuteHandler}>
                        {Constants.icons.ERROR_OUTLINE}
                    </i>
                </div>
            </div>

            <div className="input-section">
                <h1 className="highlighted">{strings.COMMUNICATE_USERS}</h1>
                <div className="switch-section">
                    <Switch
                        checked={notifyUsers}
                        checkedIcon={false}
                        disabled={false}
                        handleDiameter={20}
                        height={14}
                        offColor="#d1d1d1"
                        offHandleColor="#b3b3b3"
                        onChange={() => setNotifyUsers(!notifyUsers)}
                        onClick={() => setNotifyUsers(!notifyUsers)}
                        onColor={colors[0]}
                        onHandleColor={colors[0]}
                        uncheckedIcon={false}
                        width={30}
                    />
                    <span>{strings.SEND_NOTIFICATION_WHEN_QUIZ_PUBLISHED}</span>
                </div>
            </div>

            {autoExecute && showAutoExecuteWarning && (
                <AutoExecuteInfoModal onExited={() => setAutoExecute(false)} quizName={autoExecutableQuiz.name} />
            )}

            <div className="input-section" />
        </div>
    );
};

export default QuizInfo;
